import { Download } from 'lucide-react'

import { Button } from '@/components/ui'
import { downloadBase64File } from '@/modules/chat/utils/downloadFile'

export interface DownloadFileEntry {
  format: string
  filename: string
  contentB64?: string | null
  byteSize?: number | null
}

export interface DownloadSpec {
  title?: string | null
  rowCount?: number | null
  totalCount?: number | null
  truncated?: boolean | null
  files: DownloadFileEntry[]
}

interface DownloadArtifactProps {
  spec: DownloadSpec
}

export default function DownloadArtifact({ spec }: DownloadArtifactProps) {
  const { title, rowCount, totalCount, truncated, files } = spec
  const headline = title?.trim() || 'Transactions export'
  const subline = buildSubline(rowCount, totalCount, truncated)

  return (
    <figure className="my-1 w-full rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">{headline}</div>
          {subline && (
            <div className="text-xs text-muted-foreground">{subline}</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {files.map((file) => (
            <DownloadButton
              key={file.format}
              file={file}
              fallbackTitle={headline}
            />
          ))}
        </div>
      </div>
    </figure>
  )
}

function DownloadButton({
  file,
  fallbackTitle,
}: {
  file: DownloadFileEntry
  fallbackTitle: string
}) {
  const disabled = !file.contentB64
  const label = file.format.toUpperCase()
  const filename = file.filename || `${fallbackTitle}.${file.format}`

  return (
    <Button
      type="button"
      theme="outline"
      size="sm"
      disabled={disabled}
      onClick={() => {
        if (!file.contentB64) return
        downloadBase64File(file.contentB64, filename, file.format)
      }}
      title={
        disabled
          ? 'File expired — ask Daystrom to re-export.'
          : `Download ${filename}`
      }
    >
      <Download className="mr-1.5 size-3.5" aria-hidden />
      {label}
    </Button>
  )
}

function buildSubline(
  rowCount: number | null | undefined,
  totalCount: number | null | undefined,
  truncated: boolean | null | undefined,
): string | null {
  if (rowCount === null || rowCount === undefined) return null
  const formattedRows = rowCount.toLocaleString()
  if (truncated && totalCount && totalCount > rowCount) {
    return `${formattedRows} of ${totalCount.toLocaleString()} rows (capped)`
  }
  return `${formattedRows} rows`
}
